import {
    AccessTime,
    CheckCircle,
    NotificationsActive,
    Refresh,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { enqueueSnackbar } from "notistack";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../AppContext";
import HelmetMeta from "../../components/HelmetMeta";
import LanguageToggle from "../../components/LanguageToggle/LanguageToggle";
import LogoutButton from "../../components/LogoutButton";
import { useGetDocTypeListWithChildren } from "../../shared/hooks/useDocTypesData";
import {
    getCustomerOrderNumber,
    getExpectedReadyAt,
    isOrderDelivered,
    isOrderReady,
} from "./orderStatus";

const playReadySound = () => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const context = new AudioContext();
        const playTone = (frequency, delay) => {
            const oscillator = context.createOscillator();
            const gain = context.createGain();
            oscillator.type = "sine";
            oscillator.frequency.value = frequency;
            gain.gain.setValueAtTime(0.0001, context.currentTime + delay);
            gain.gain.exponentialRampToValueAtTime(
                0.25,
                context.currentTime + delay + 0.02
            );
            gain.gain.exponentialRampToValueAtTime(
                0.0001,
                context.currentTime + delay + 0.28
            );
            oscillator.connect(gain);
            gain.connect(context.destination);
            oscillator.start(context.currentTime + delay);
            oscillator.stop(context.currentTime + delay + 0.3);
        };
        playTone(660, 0);
        playTone(880, 0.34);
        window.setTimeout(() => context.close?.(), 1000);
    } catch (_) {
        // Browsers can block sound until the display has received user interaction.
    }
};

const OrderStatusCard = ({ order, ready, now, onDelivered, isDelivering, t }) => {
    const expectedAt = getExpectedReadyAt(order);
    const remainingMinutes = expectedAt
        ? Math.max(0, Math.ceil((expectedAt.getTime() - now) / 60000))
        : null;
    const expectedLabel = expectedAt
        ? new Intl.DateTimeFormat(undefined, {
              hour: "2-digit",
              minute: "2-digit",
          }).format(expectedAt)
        : "—";

    return (
        <Card
            elevation={0}
            sx={{
                height: "100%",
                color: ready ? "#0f5132" : "#664d03",
                bgcolor: ready ? "#d1e7dd" : "#fff3cd",
                border: "3px solid",
                borderColor: ready ? "#198754" : "#ffc107",
                borderRadius: 4,
                transition: "background-color 300ms ease, border-color 300ms ease",
            }}
        >
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 }, height: "100%" }}>
                <Stack spacing={2.5} sx={{ height: "100%" }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="overline" sx={{ fontWeight: 900 }}>
                                {t("kioskPreview.orderNumber")}
                            </Typography>
                            <Typography
                                variant="h2"
                                sx={{ fontWeight: 950, lineHeight: 1 }}
                            >
                                {getCustomerOrderNumber(order)}
                            </Typography>
                        </Box>
                        <Chip
                            icon={ready ? <CheckCircle /> : <AccessTime />}
                            label={
                                ready
                                    ? t("kioskPreview.ready")
                                    : t("kioskPreview.preparing")
                            }
                            color={ready ? "success" : "warning"}
                            sx={{ fontWeight: 900, fontSize: 16, height: 40 }}
                        />
                    </Box>

                    {order.customer_name && (
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            {order.customer_name}
                        </Typography>
                    )}

                    <Box sx={{ mt: "auto !important" }}>
                        {ready ? (
                            <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                {t("kioskPreview.collectOrder")}
                            </Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                <Typography variant="body1" sx={{ fontWeight: 800 }}>
                                    {t("kioskPreview.expectedAt", {
                                        time: expectedLabel,
                                    })}
                                </Typography>
                                {remainingMinutes !== null && (
                                    <Typography variant="body2">
                                        {t("kioskPreview.minutesRemaining", {
                                            count: remainingMinutes,
                                        })}
                                    </Typography>
                                )}
                            </Stack>
                        )}
                    </Box>

                    {ready && (
                        <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            color="success"
                            disabled={isDelivering}
                            onClick={() => onDelivered(order)}
                            sx={{ py: 1.5, fontWeight: 900, fontSize: 18 }}
                        >
                            {isDelivering
                                ? t("kioskPreview.removing")
                                : t("kioskPreview.delivered")}
                        </Button>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default function KioskPreview() {
    const { t } = useTranslation();
    const { socket, company, posProfile } = useContext(AppContext);
    const [now, setNow] = useState(Date.now());
    const readySnapshotRef = useRef(null);
    const filters = useMemo(() => {
        const result = [
            ["status", "=", "Attending"],
            ["docstatus", "!=", 2],
        ];
        if (company?.name) result.push(["company", "=", company.name]);
        if (posProfile?.name) result.push(["pos_profile", "=", posProfile.name]);
        return result;
    }, [company?.name, posProfile?.name]);

    const {
        data: fetchedOrders,
        isLoading,
        isError,
        refetch,
    } = useGetDocTypeListWithChildren({
        doctype:
            company?.name && posProfile?.name ? "Table Order" : null,
        filters,
        order_by: "creation asc",
    });

    const orders = useMemo(
        () => (fetchedOrders || []).filter((order) => !isOrderDelivered(order)),
        [fetchedOrders]
    );
    const preparingOrders = useMemo(
        () => orders.filter((order) => !isOrderReady(order)),
        [orders]
    );
    const readyOrders = useMemo(() => orders.filter(isOrderReady), [orders]);

    const deliveredMutation = useMutation(async (order) => {
        const { data } = await axios.post(
            "method/restaurant_management.api.mark_kiosk_order_delivered",
            { order_name: order.name }
        );
        return data?.message;
    });

    const handleDelivered = (order) => {
        deliveredMutation.mutate(order, {
            onSuccess: () => refetch(),
            onError: () =>
                enqueueSnackbar(t("kioskPreview.deliveryError"), {
                    variant: "error",
                }),
        });
    };

    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 30000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        const currentReady = new Set(readyOrders.map((order) => order.name));
        if (readySnapshotRef.current === null) {
            readySnapshotRef.current = currentReady;
            return;
        }
        const newlyReady = [...currentReady].filter(
            (name) => !readySnapshotRef.current.has(name)
        );
        if (newlyReady.length > 0) playReadySound();
        readySnapshotRef.current = currentReady;
    }, [readyOrders]);

    useEffect(() => {
        const poll = window.setInterval(refetch, 10000);
        if (!socket?.on) return () => window.clearInterval(poll);

        const refreshOrders = (event) => {
            if (!event?.doctype || event.doctype === "Table Order") refetch();
        };
        socket.emit("doctype_subscribe", "Table Order");
        socket.on("list_update", refreshOrders);
        socket.on("kiosk_order_delivered", refreshOrders);
        return () => {
            window.clearInterval(poll);
            socket.off?.("list_update", refreshOrders);
            socket.off?.("kiosk_order_delivered", refreshOrders);
            socket.emit("doctype_unsubscribe", "Table Order");
        };
    }, [socket, refetch]);

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6f8", py: 3 }}>
            <HelmetMeta title={t("kioskPreview.helmetTitle")} />
            <Container maxWidth="xl">
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box>
                        <Typography variant="h3" sx={{ fontWeight: 950 }}>
                            {t("kioskPreview.title")}
                        </Typography>
                        <Typography color="text.secondary">
                            {t("kioskPreview.subtitle")}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <LanguageToggle />
                        <Button
                            variant="outlined"
                            startIcon={<Refresh />}
                            onClick={() => refetch()}
                        >
                            {t("common.refresh")}
                        </Button>
                        <LogoutButton />
                    </Stack>
                </Box>

                {!posProfile?.name ? (
                    <Alert severity="warning">
                        {t("kioskPreview.configurationError")}
                    </Alert>
                ) : isLoading ? (
                    <Box sx={{ display: "grid", placeItems: "center", py: 12 }}>
                        <CircularProgress />
                    </Box>
                ) : isError ? (
                    <Alert severity="error">{t("kioskPreview.loadError")}</Alert>
                ) : orders.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 14 }}>
                        <NotificationsActive sx={{ fontSize: 72, color: "text.disabled" }} />
                        <Typography variant="h4" sx={{ mt: 2, fontWeight: 900 }}>
                            {t("kioskPreview.noOrders")}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={4}>
                        {readyOrders.length > 0 && (
                            <Box>
                                <Typography variant="h4" sx={{ mb: 2, fontWeight: 900 }}>
                                    {t("kioskPreview.readyOrders")}
                                </Typography>
                                <Grid container spacing={2.5}>
                                    {readyOrders.map((order) => (
                                        <Grid item xs={12} sm={6} md={4} key={order.name}>
                                            <OrderStatusCard
                                                order={order}
                                                ready
                                                now={now}
                                                t={t}
                                                onDelivered={handleDelivered}
                                                isDelivering={
                                                    deliveredMutation.isLoading &&
                                                    deliveredMutation.variables?.name === order.name
                                                }
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                        {preparingOrders.length > 0 && (
                            <Box>
                                <Typography variant="h4" sx={{ mb: 2, fontWeight: 900 }}>
                                    {t("kioskPreview.preparingOrders")}
                                </Typography>
                                <Grid container spacing={2.5}>
                                    {preparingOrders.map((order) => (
                                        <Grid item xs={12} sm={6} md={4} key={order.name}>
                                            <OrderStatusCard
                                                order={order}
                                                ready={false}
                                                now={now}
                                                t={t}
                                                onDelivered={handleDelivered}
                                                isDelivering={false}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </Stack>
                )}
            </Container>
        </Box>
    );
}
